import {
  GenData,
  IGenEq,
  EGenMode,
  IGenSingle,
  IGenMulti,
  eqToSingle,
  eqToMulti,
  QSingle,
  QMulti,
} from '@educraft/base';
import { ExerciseData } from '../exercise';
import { ArchiveGenMulti, configureArchiveGenMulti } from '../../genMulti/generators/archiveGenMulti';

/** Create a combined IGenSingle from selected IGenEq names via Provider */
export function makeCombinedSingle(data: ExerciseData): IGenSingle {
  const {selEq} = data.item;
  const generators: IGenSingle[] = selEq.map((name) => {
    const eq = data.provider.getOne<IGenEq>(EGenMode.GenEq, name);
    return eqToSingle(eq);
  });
  return {
    generate(genData: GenData): QSingle {
      const idx = genData.random.next(0, generators.length);
      return generators[idx].generate(genData);
    },
  };
}

/** Create a combined IGenMulti from selected names via Provider.
 * Tries GenMulti first, then archive map, then falls back to GenEq + eqToMulti. */
export function makeCombinedMulti(data: ExerciseData): IGenMulti {
  const selAbc = data.item.selAbc.length > 0 ? data.item.selAbc : data.item.selEq;
  const generators: IGenMulti[] = selAbc.map((name) => {
    const multi = data.provider.getOneOrNull<IGenMulti>(EGenMode.GenMulti, name);
    if (multi) return multi;
    const archiveInfo = data.item.archiveMap?.[name];
    if (archiveInfo) {
      const archiveId = `${archiveInfo.archive}:${archiveInfo.file}`;
      configureArchiveGenMulti(archiveId, archiveInfo.archive, archiveInfo.file);
      return new ArchiveGenMulti(archiveId);
    }
    const eq = data.provider.getOne<IGenEq>(EGenMode.GenEq, name);
    return eqToMulti(eq);
  });
  return {
    generate(genData: GenData): QMulti {
      const idx = genData.random.next(0, generators.length);
      return generators[idx].generate(genData);
    },
  };
}

/**
 * Generate unique items from a generator, like C# UniqueList.CreateCount().
 * Generates up to `count` unique items (by toKey), falls back to random fill if not enough unique.
 */
export function generateUnique<T>(
  gen: { generate(data: GenData): T },
  genData: GenData,
  count: number,
  toKey: (item: T) => string,
  preSamples?: T[]
): T[] {
  const dict = new Map<string, T>();

  // Add any pre-generated samples
  if (preSamples) {
    for (const item of preSamples) {
      const key = toKey(item);
      if (!dict.has(key)) dict.set(key, item);
    }
  }

  let tries = 0;
  while (dict.size < count && tries < 10_000) {
    const item = gen.generate(genData);
    const key = toKey(item);
    if (!dict.has(key)) dict.set(key, item);
    tries++;
  }

  const items = Array.from(dict.values());

  // Fill remaining with random picks (avoid consecutive duplicates)
  let prevKey = '';
  while (items.length < count) {
    const item = gen.generate(genData);
    const key = toKey(item);
    if (key !== prevKey) {
      items.push(item);
      prevKey = key;
    }
  }

  return items.slice(0, count);
}
